import React from "react";
import { RefreshCw, Calendar } from "lucide-react";
import { GreetingSkeleton } from "./skeletons";

/**
 * GreetingBlock — page header above the KPI strip.
 *
 * Overline: today's date + dot + active-journeys count
 * Heading:  Good {timeOfDay}, {firstName}. (28px Manrope, indigo dot accent)
 * Sub:      contextual one-liner (e.g. "1 train in transit, 2 waitlists moving")
 * Right:    Refresh all + Sync calendar secondary buttons
 *
 * Props:
 *   user, activeJourneys, subtitle, onRefreshAll, onSyncCalendar,
 *   isRefreshing, isSyncingCal
 */

const greetingFor = (now = new Date()) => {
  // Use IST hour for greeting selection since users are India-based.
  const istHour = Number(
    now.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      hour12: false,
    })
  );
  if (istHour < 5) return "Up late";
  if (istHour < 12) return "Good morning";
  if (istHour < 17) return "Good afternoon";
  if (istHour < 21) return "Good evening";
  return "Good night";
};

const formatDate = (d = new Date()) =>
  d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const GreetingBlock = ({
  user,
  activeJourneys = 0,
  subtitle,
  onRefreshAll,
  onSyncCalendar,
  isRefreshing = false,
  isSyncingCal = false,
  loading = false,
}) => {
  if (loading) return <GreetingSkeleton />;

  const firstName = (user?.name || "").split(/\s+/)[0] || "there";
  const greeting = greetingFor();
  const dateStr = formatDate();

  return (
    <div
      className="flex items-end justify-between gap-4 flex-wrap"
      data-testid="greeting-block"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10.5px] tracking-[0.18em] uppercase text-zinc-500 font-semibold font-mono">
            {dateStr}
          </span>
          <span className="w-1 h-1 rounded-full bg-zinc-700" />
          <span className="text-[10.5px] tracking-[0.18em] uppercase text-emerald-400 font-semibold font-mono">
            {activeJourneys} Active Journeys
          </span>
        </div>
        <h1
          className="text-[26px] sm:text-[28px] leading-tight font-bold text-zinc-50 font-heading"
          style={{ letterSpacing: "-0.02em" }}
        >
          {greeting}, {firstName}
          <span className="text-indigo-400">.</span>
        </h1>
        {subtitle && (
          <p className="text-[12.5px] text-zinc-400 mt-1.5 max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {onRefreshAll && (
          <button
            type="button"
            onClick={onRefreshAll}
            disabled={isRefreshing}
            data-testid="refresh-all-status-btn"
            className="h-9 px-3 rounded-lg bg-zinc-900/70 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-zinc-100 text-[12px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={12}
              className={isRefreshing ? "animate-spin" : ""}
            />
            Refresh all
          </button>
        )}
        {onSyncCalendar && (
          <button
            type="button"
            onClick={onSyncCalendar}
            disabled={isSyncingCal}
            data-testid="sync-all-calendar-btn"
            className="h-9 px-3 rounded-lg bg-zinc-900/70 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-zinc-100 text-[12px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <Calendar size={12} />
            Sync calendar
          </button>
        )}
      </div>
    </div>
  );
};

export default GreetingBlock;
